import { useState, useEffect } from 'react';
import { captureError } from "@/lib/logger";
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Loader2, X, Check, ChevronRight, ChevronLeft,
    Receipt, ClipboardCheck, Wallet, Trash2,
    Fuel, Wifi, Users, Wrench, Home, Gift, FileCheck, MapPin, Package, Truck, MoreHorizontal, CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatBusinessDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import TransactionSelector from '@/components/ui/TransactionSelector';

const EXPENSE_TYPES = [
    { value: 'auto_parts', label: 'Auto Parts', icon: Wrench },
    { value: 'family', label: 'Family', icon: Users },
    { value: 'fuel', label: 'Fuel', icon: Fuel },
    { value: 'internet', label: 'Internet', icon: Wifi },
    { value: 'office', label: 'Office', icon: Home },
    { value: 'referral', label: 'Referral', icon: Gift },
    { value: 'refund', label: 'Refund', icon: CreditCard },
    { value: 'roadworthiness', label: 'Roadworthiness', icon: FileCheck },
    { value: 'salary', label: 'Salary', icon: Users },
    { value: 'tracker', label: 'Tracker', icon: MapPin },
    { value: 'transportation', label: 'Transportation', icon: Truck },
    { value: 'other', label: 'Other', icon: MoreHorizontal },
] as const;

const STEPS = [
    { key: 'details', label: 'Details', icon: Receipt, description: 'Type & Description' },
    { key: 'transaction', label: 'Transaction', icon: Wallet, description: 'Link Payment' },
    { key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Confirm Details' },
];

const variants = {
    enter: (direction: number) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({ x: direction > 0 ? -50 : 50, opacity: 0 }),
};

interface AddExpenseSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    expenseId?: number | null;
    onSuccess?: () => void;
}

export default function AddExpenseSheet({
    open,
    onOpenChange,
    expenseId,
    onSuccess,
}: AddExpenseSheetProps) {
    const queryClient = useQueryClient();
    const isEditMode = !!expenseId;
    const [step, setStep] = useState(0);
    const [direction, setDirection] = useState(0);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [state, setState] = useState({
        type: 'fuel' as string,
        description: '',
        transactionId: null as number | null,
    });

    const [transactionDetails, setTransactionDetails] = useState<{
        amount?: number;
        transaction_at?: string;
    } | null>(null);

    const updateState = (updates: Partial<typeof state>) => {
        setState(prev => ({ ...prev, ...updates }));
    };

    // Fetch existing expense for edit mode
    const { data: existingData } = useQuery({
        queryKey: ['expense-edit', expenseId],
        queryFn: async () => {
            if (!expenseId) return null;
            const { data, error } = await supabase
                .from('expenses')
                .select('id, type, description, transaction_id, transactions(amount, transaction_at)')
                .eq('id', expenseId)
                .single();
            if (error) throw error;
            return data;
        },
        enabled: open && isEditMode,
    });

    // Populate form when editing
    useEffect(() => {
        if (open && existingData && isEditMode) {
            setState({
                type: existingData.type || 'fuel',
                description: existingData.description || '',
                transactionId: existingData.transaction_id || null,
            });
            if (existingData.transactions) {
                setTransactionDetails({
                    amount: existingData.transactions.amount,
                    transaction_at: existingData.transactions.transaction_at,
                });
            }
        }
    }, [existingData, isEditMode, open]);

    const resetAndClose = () => {
        setStep(0);
        setState({ type: 'fuel', description: '', transactionId: null });
        setTransactionDetails(null);
        setShowCloseConfirm(false);
        setShowDeleteConfirm(false);
        onOpenChange(false);
    };

    useEffect(() => {
        if (!open) {
            setStep(0);
            setState({ type: 'fuel', description: '', transactionId: null });
            setTransactionDetails(null);
        }
    }, [open]);

    const canGoNext = () => {
        switch (step) {
            case 0: return state.type !== '' && state.description.trim() !== '';
            case 1: return state.transactionId !== null;
            case 2: return true;
            default: return false;
        }
    };

    const handleNext = () => {
        if (step < STEPS.length - 1) {
            setDirection(1);
            setStep(step + 1);
        }
    };

    const handleBack = () => {
        if (step > 0) {
            setDirection(-1);
            setStep(step - 1);
        }
    };

    const isDirty = () => {
        if (isEditMode && existingData) {
            return (
                state.type !== (existingData.type || 'fuel') ||
                state.description !== (existingData.description || '') ||
                state.transactionId !== (existingData.transaction_id || null)
            );
        }
        // Add mode: dirty if user has entered anything
        return state.description.trim() !== '' || state.transactionId !== null || state.type !== 'fuel';
    };

    const handleClose = () => {
        if (isDirty()) {
            setShowCloseConfirm(true);
        } else {
            onOpenChange(false);
        }
    };

    const formatCurrency = (amount: number) =>
        new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

    // Save mutation (insert or update)
    const mutation = useMutation({
        mutationFn: async () => {
            const payload = {
                type: state.type,
                description: state.description,
                transaction_id: state.transactionId!,
            };

            if (isEditMode && expenseId) {
                const { error } = await supabase
                    .from('expenses')
                    .update(payload)
                    .eq('id', expenseId);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('expenses')
                    .insert(payload);
                if (error) throw error;
            }
        },
        onSuccess: () => {
            toast.success(isEditMode ? 'Expense updated' : 'Expense added');
            queryClient.invalidateQueries({ queryKey: ['expenses'] });
            queryClient.invalidateQueries({ queryKey: ['expenses-count'] });
            queryClient.invalidateQueries({ queryKey: ['expense-types'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            resetAndClose();
            onSuccess?.();
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(error.message || 'Failed to save expense');
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!expenseId) throw new Error('No expense to delete');
            const { error } = await supabase
                .from('expenses')
                .delete()
                .eq('id', expenseId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Expense deleted');
            queryClient.invalidateQueries({ queryKey: ['expenses'] });
            queryClient.invalidateQueries({ queryKey: ['expenses-count'] });
            queryClient.invalidateQueries({ queryKey: ['expense-types'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            resetAndClose();
            onSuccess?.();
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(error.message || 'Failed to delete expense');
        },
    });

    const selectedTypeInfo = EXPENSE_TYPES.find(t => t.value === state.type) || EXPENSE_TYPES[0];
    const TypeIcon = selectedTypeInfo.icon;

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <div className="space-y-6">
                        <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Expense Type *</Label>
                            <Select value={state.type} onValueChange={(v) => updateState({ type: v })}>
                                <SelectTrigger className="h-11 mt-2">
                                    <SelectValue placeholder="Select type..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {EXPENSE_TYPES.map((t) => {
                                        const Icon = t.icon;
                                        return (
                                            <SelectItem key={t.value} value={t.value}>
                                                <div className="flex items-center gap-2">
                                                    <Icon className="h-4 w-4" />
                                                    {t.label}
                                                </div>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor="description">Description *</Label>
                            <Textarea
                                id="description"
                                value={state.description}
                                onChange={(e) => updateState({ description: e.target.value })}
                                placeholder="What is this expense for?"
                                rows={4}
                                className="mt-2"
                            />
                        </div>
                    </div>
                );
            case 1:
                return (
                    <div className="space-y-6">
                        <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Transaction *</Label>
                            <p className="text-xs text-muted-foreground mb-3">Link the payment transaction for this expense.</p>
                            <TransactionSelector
                                value={state.transactionId}
                                onChange={(id, transaction) => {
                                    updateState({ transactionId: id });
                                    if (transaction) {
                                        setTransactionDetails({
                                            amount: transaction.amount,
                                            transaction_at: transaction.transaction_at,
                                        });
                                    } else {
                                        setTransactionDetails(null);
                                    }
                                }}
                                defaultType="DEBIT"
                                placeholder="Select transaction..."
                            />
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-card border rounded-xl shadow-sm overflow-hidden relative">
                            <div className="h-2 bg-gradient-to-r from-primary/50 to-primary w-full" />
                            <div className="p-6 space-y-4">
                                <div className="flex justify-between items-center py-2 border-b">
                                    <span className="text-muted-foreground">Type</span>
                                    <span className="font-medium capitalize flex items-center gap-2">
                                        <TypeIcon className="h-4 w-4" />
                                        {selectedTypeInfo.label}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b">
                                    <span className="text-muted-foreground">Description</span>
                                    <span className="font-medium text-right max-w-[200px] truncate">{state.description}</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b">
                                    <span className="text-muted-foreground">Amount</span>
                                    <span className="font-medium text-destructive">
                                        {transactionDetails?.amount ? formatCurrency(transactionDetails.amount) : '-'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-2">
                                    <span className="text-muted-foreground">Date</span>
                                    <span className="font-medium">
                                        {formatBusinessDate(transactionDetails?.transaction_at)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-4xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
                >
                    {/* Left Sidebar - Progress */}
                    <div className="w-1/3 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
                        {/* Decorative background elements */}
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

                        <div className="mb-8 relative z-10">
                            <h2 className="text-2xl font-semibold tracking-tight">
                                {isEditMode ? 'Edit Expense' : 'Add Expense'}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {isEditMode ? 'Update the expense record details.' : 'Record a new business expense.'}
                            </p>
                        </div>

                        <div className="space-y-1 relative z-10 flex-1">
                            {STEPS.map((s, i) => {
                                const Icon = s.icon;
                                const isActive = i === step;
                                const isCompleted = i < step;
                                return (
                                    <div
                                        key={s.key}
                                        className={cn(
                                            "group flex items-center gap-3 p-3 rounded-xl transition-all duration-300",
                                            isActive ? "bg-background shadow-sm ring-1 ring-border" : "hover:bg-background/50",
                                            isCompleted ? "opacity-70" : "opacity-50",
                                            isActive && "!opacity-100"
                                        )}
                                    >
                                        <div className={cn(
                                            "h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300",
                                            isActive ? "bg-primary text-primary-foreground" :
                                                isCompleted ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground",
                                        )}>
                                            {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                                        </div>
                                        <div>
                                            <p className={cn(
                                                "font-medium text-sm transition-colors",
                                                isActive ? "text-foreground" : "text-muted-foreground"
                                            )}>
                                                {s.label}
                                            </p>
                                            <p className="text-xs text-muted-foreground/70 hidden lg:block">
                                                {s.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Delete button in edit mode */}
                        {isEditMode && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="mt-4 w-fit text-destructive hover:text-destructive hover:bg-destructive/10 p-0 h-auto"
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={deleteMutation.isPending}
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Expense
                            </Button>
                        )}

                        <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
                            onClick={handleClose}
                        >
                            <X className="h-4 w-4 mr-2" />
                            Cancel & Close
                        </Button>
                    </div>

                    {/* Right Content */}
                    <div className="flex-1 flex flex-col h-full relative">
                        {/* Mobile Header */}
                        <div className="md:hidden p-4 border-b flex items-center justify-between">
                            <div>
                                <h2 className="font-semibold">Step {step + 1} of {STEPS.length}</h2>
                                <p className="text-xs text-muted-foreground">{STEPS[step].label}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={handleClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Main Form Area */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10">
                            <AnimatePresence mode="wait" initial={false} custom={direction}>
                                <motion.div
                                    key={step}
                                    custom={direction}
                                    variants={variants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{
                                        x: { type: "spring", stiffness: 300, damping: 30 },
                                        opacity: { duration: 0.2 }
                                    }}
                                    className="h-full max-w-2xl mx-auto"
                                >
                                    <div className="mb-6">
                                        <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                                        <p className="text-muted-foreground">{STEPS[step].description}</p>
                                    </div>
                                    {renderStep()}
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Sticky Footer */}
                        <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
                            <div className="max-w-2xl mx-auto flex items-center justify-between">
                                <Button
                                    variant="outline"
                                    onClick={handleBack}
                                    disabled={step === 0 || mutation.isPending}
                                    className="w-24 rounded-full"
                                >
                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                    Back
                                </Button>
                                {step < STEPS.length - 1 ? (
                                    <Button
                                        onClick={handleNext}
                                        disabled={!canGoNext()}
                                        className="w-32 rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => mutation.mutate()}
                                        disabled={mutation.isPending}
                                        className="w-40 rounded-full shadow-lg hover:shadow-emerald-500/25 transition-all bg-emerald-600 hover:bg-emerald-700"
                                    >
                                        {mutation.isPending ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Check className="h-4 w-4 mr-2" />
                                        )}
                                        {isEditMode ? 'Save Changes' : 'Add Expense'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Close Confirmation Dialog */}
            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to discard them?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Continue Editing</AlertDialogCancel>
                        <AlertDialogAction onClick={resetAndClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete expense?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete this expense record. The linked transaction will not be affected.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteMutation.mutate()}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteMutation.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
